import { useState } from 'react';
import { motion } from 'framer-motion';

const CalendarPicker = ({ onSelect, onClose, selectedDate, weeklyOffDays = '' }) => {
  const [date, setDate] = useState(selectedDate || new Date());

  const handlePrevMonth = () => setDate(new Date(date.getFullYear(), date.getMonth() - 1, 1));
  const handleNextMonth = () => setDate(new Date(date.getFullYear(), date.getMonth() + 1, 1));

  const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const maxDate = new Date(today);
  maxDate.setDate(today.getDate() + 7);

  // Parse weekly off days (semicolon-separated string like "Saturday;Sunday")
  const offDays = weeklyOffDays
    ? weeklyOffDays.split(';').map((day) => day.trim().toLowerCase())
    : [];

  // Check if a date is a weekly off day
  const isWeeklyOffDay = (checkDate) => {
    if (offDays.length === 0) return false;
    const dayName = checkDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    return offDays.includes(dayName);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">
            {date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h3>
          <div className="flex gap-2">
            <button onClick={handlePrevMonth} className="p-2 rounded-full hover:bg-slate-100">
              &lt;
            </button>
            <button onClick={handleNextMonth} className="p-2 rounded-full hover:bg-slate-100">
              &gt;
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1 text-center text-sm text-slate-500 mb-2">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day) => (
            <div key={day}>{day}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: firstDayOfMonth }).map((_, i) => (
            <div key={`empty-${i}`}></div>
          ))}
          {Array.from({ length: daysInMonth }).map((_, day) => {
            const dayDate = new Date(date.getFullYear(), date.getMonth(), day + 1);
            const isPast = dayDate <= today;
            const isTooFar = dayDate > maxDate;
            const isOffDay = isWeeklyOffDay(dayDate);
            const isDisabled = isPast || isTooFar || isOffDay;
            const isSelected =
              selectedDate && dayDate.toDateString() === selectedDate.toDateString();

            return (
              <button
                key={day}
                disabled={isDisabled}
                onClick={() => onSelect(dayDate)}
                className={`p-2 rounded-full text-center transition-colors ${
                  isDisabled
                    ? isOffDay
                      ? 'text-red-300 cursor-not-allowed line-through'
                      : 'text-slate-300 cursor-not-allowed'
                    : isSelected
                      ? 'bg-orange-600 text-white font-bold'
                      : 'hover:bg-slate-100'
                }`}
                title={isOffDay ? 'Weekly Off Day' : ''}
              >
                {day + 1}
              </button>
            );
          })}
        </div>

        <button
          onClick={onClose}
          className="w-full mt-4 p-2 bg-slate-100 hover:bg-slate-200 rounded-lg font-semibold text-slate-700"
        >
          Close
        </button>
      </motion.div>
    </motion.div>
  );
};

export default CalendarPicker;
